import { cn } from "@repo/shad"
import { AnimatePresence, LayoutGroup, motion } from "motion/react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"

type ExpandableCardContextValue = {
  selectedId: string | null
  onClose: () => void
  registerTrigger: (id: string, el: HTMLElement | null) => void
  getTriggerRect: (id: string) => DOMRect | null
}

const ExpandableCardContext = createContext<ExpandableCardContextValue | null>(null)

export function useExpandableCard() {
  const ctx = useContext(ExpandableCardContext)
  if (!ctx) throw new Error("useExpandableCard must be used within ExpandableCard.Root")
  return ctx
}

/* -- Root -- */

function Root({
  selectedId,
  onClose,
  children,
}: {
  selectedId: string | null
  onClose: () => void
  children: ReactNode
}) {
  const triggerRefs = useRef(new Map<string, HTMLElement>())

  const registerTrigger = (id: string, el: HTMLElement | null) => {
    if (el) {
      triggerRefs.current.set(id, el)
    } else {
      triggerRefs.current.delete(id)
    }
  }

  const getTriggerRect = (id: string): DOMRect | null => {
    const el = triggerRefs.current.get(id)
    return el ? el.getBoundingClientRect() : null
  }

  useEffect(() => {
    if (!selectedId) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [selectedId, onClose])

  useEffect(() => {
    if (!selectedId) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [selectedId])

  return (
    <ExpandableCardContext.Provider
      value={{ selectedId, onClose, registerTrigger, getTriggerRect }}
    >
      <LayoutGroup>{children}</LayoutGroup>
    </ExpandableCardContext.Provider>
  )
}

/* -- Trigger -- */

function Trigger({
  cardId,
  children,
  className,
  onClick,
}: {
  cardId: string
  children: ReactNode
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
}) {
  const { selectedId, registerTrigger } = useExpandableCard()
  const isSelected = selectedId === cardId
  const [height, setHeight] = useState<number | undefined>(undefined)

  const measureRef = useCallback((el: HTMLElement | null) => {
    if (el) setHeight(el.getBoundingClientRect().height)
  }, [])

  return (
    <div
      ref={(el: HTMLElement | null) => registerTrigger(cardId, el)}
      style={isSelected && height ? { height } : undefined}
    >
      {!isSelected && (
        <motion.div
          ref={measureRef}
          layoutId={`expandable-card-${cardId}`}
          className={className}
          onClick={onClick}
          transition={{ type: "spring", stiffness: 400, damping: 30, mass: 0.8 }}
        >
          {children}
        </motion.div>
      )}
    </div>
  )
}

/* -- Overlay -- */

const ANCHOR_GAP = 8

function Overlay({
  children,
  className,
  anchor = false,
}: {
  children: ReactNode
  className?: string
  anchor?: boolean
}) {
  const { selectedId, onClose, getTriggerRect } = useExpandableCard()
  const isAnchored = anchor && !!selectedId && window.innerWidth >= 640

  // Compute anchor position from trigger's viewport rect
  let anchorStyle: React.CSSProperties | undefined
  let flipAbove = false
  if (isAnchored) {
    const rect = getTriggerRect(selectedId)
    if (rect) {
      const vh = window.innerHeight
      const spaceBelow = vh - rect.bottom
      flipAbove = spaceBelow < 200
      const vw = window.innerWidth
      const margin = 8
      const rightAligned = rect.left > vw / 2
      const horizontal = rightAligned
        ? { right: Math.max(margin, vw - rect.right) }
        : { left: Math.max(margin, rect.left) }
      anchorStyle = flipAbove
        ? { bottom: vh - rect.top + ANCHOR_GAP, ...horizontal }
        : { top: rect.bottom + ANCHOR_GAP, ...horizontal }
    }
  }

  const content = (
    <>
      <AnimatePresence>
        {selectedId && (
          <motion.div
            key="expandable-card-backdrop"
            className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={onClose}
            onPointerDown={(e) => e.stopPropagation()}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {selectedId && (
          <motion.div
            key={`expandable-card-overlay-${selectedId}`}
            layoutId={`expandable-card-${selectedId}`}
            className={cn(
              "fixed z-[9999] overflow-hidden overscroll-contain bg-card border shadow-2xl rounded-xl",
              !isAnchored && "top-[12vh] left-0 right-0 mx-auto",
              className,
            )}
            style={{ borderRadius: 12, ...(isAnchored ? anchorStyle : undefined) }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
              mass: 0.8,
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <motion.div layout="position">{children}</motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )

  return createPortal(content, document.body)
}

export const ExpandableCard = { Root, Trigger, Overlay }
